import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Typography, Switch, FormControlLabel, Button, CircularProgress, Alert,
} from "@mui/material";
import Swal from "sweetalert2";
import { getSlipBotSettings, updateSlipBotSettings } from "api/slipBot";

const ALL_CURRENCIES = ["LAK", "THB", "USD"];

// Admin toggle for which currencies the WhatsApp slip bot may MATCH amounts
// in (backend/services/slipMatcher.js expandTargets). Matching only — the
// "is this a bank slip?" classifier is unaffected, so disabling a currency
// never stops a real slip from being recognized; it just can't create a
// top-up in that currency.
const SlipBotSettings = () => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(null); // Set of enabled currencies, null = loading
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await getSlipBotSettings();
      if (res && res.success) {
        setEnabled(new Set(res.data.matchCurrencies));
      } else {
        setEnabled(new Set(ALL_CURRENCIES));
        Swal.fire({ icon: "error", text: (res && res.message) || "Failed to load settings", timer: 2500, toast: true, position: "top-right", showConfirmButton: false });
      }
    })();
  }, []);

  const toggle = (currency) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(currency)) {
        if (next.size === 1) return prev; // never allow zero currencies
        next.delete(currency);
      } else {
        next.add(currency);
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    const res = await updateSlipBotSettings([...enabled]);
    setSaving(false);
    Swal.fire({
      icon: res && res.success ? "success" : "error",
      text: res && res.success ? t("settings.slipBot.saved") : (res && res.message) || "Save failed",
      timer: 2000, toast: true, position: "top-right", showConfirmButton: false,
    });
  };

  if (enabled === null) {
    return <Box sx={{ p: 3 }}><CircularProgress color="secondary" /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 560, py: 2 }}>
      <Typography variant="h6" color="secondary" sx={{ mb: 1 }}>
        {t("settings.slipBot.matchCurrenciesTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("settings.slipBot.matchCurrenciesHelp")}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
        {ALL_CURRENCIES.map((currency) => (
          <FormControlLabel
            key={currency}
            control={
              <Switch
                color="secondary"
                checked={enabled.has(currency)}
                onChange={() => toggle(currency)}
              />
            }
            label={t(`settings.slipBot.currency${currency}`)}
          />
        ))}
      </Box>

      {enabled.size === 1 && (
        <Alert severity="info" sx={{ mb: 2 }}>{t("settings.slipBot.atLeastOneCurrency")}</Alert>
      )}

      <Button variant="contained" color="secondary" onClick={save} disabled={saving}>
        {saving ? <CircularProgress size={20} color="inherit" /> : t("settings.slipBot.save")}
      </Button>
    </Box>
  );
};

export default SlipBotSettings;
